import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, FileText, ChevronUp, ChevronDown } from "lucide-react";
// Bild-basierter PDF-Viewer (rendert Seiten als PNG über das Backend) — funktioniert
// zuverlässig auf Mobil/iPad, wo das native <iframe>-PDF nicht blättert.
// `basePath` z.B. "/documents/<id>" oder "/ok-documents/<id>".
export default function PagedPdfViewer({ basePath, jump }) {
  const [count, setCount] = useState(0);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [current, setCurrent] = useState(1);
  const scrollRef = useRef(null);
  const pageRefs = useRef([]);
  const lastJump = useRef(null);

  useEffect(() => {
    let urls = []; let cancelled = false;
    setPages([]); setCount(0); setLoading(true); setError(false); setCurrent(1);
    pageRefs.current = [];
    lastJump.current = null;
    (async () => {
      try {
        const info = await api.get(`${basePath}/pages`);
        const n = Math.min(info.data.page_count || 0, 60);
        setCount(n);
        for (let i = 0; i < n; i++) {
          if (cancelled) break;
          const r = await api.get(`${basePath}/page/${i}.png`, { responseType: "blob" });
          const u = URL.createObjectURL(r.data);
          urls.push(u);
          setPages((p) => [...p, u]);
        }
      } catch { if (!cancelled) setError(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [basePath]);

  // Nur den INTERNEN PDF-Container scrollen (niemals äußere Container wie den Editor bewegen).
  const scrollToPage = (idx) => {
    const cont = scrollRef.current;
    const el = pageRefs.current[idx];
    if (!cont || !el) return;
    const contRect = cont.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    cont.scrollTo({ top: cont.scrollTop + (elRect.top - contRect.top), behavior: "smooth" });
  };

  // Sprung zur Zielseite (aus dem Editor-Accordion) — pro `nonce` nur EINMAL,
  // damit normale Re-Renders (z.B. Tippen im Editor) den Scroll NICHT zurücksetzen.
  const jumpNonce = jump?.nonce;
  const jumpPage = jump?.page;
  useEffect(() => {
    if (!jumpNonce || !jumpPage) return;
    if (lastJump.current === jumpNonce) return;
    if (!pageRefs.current[jumpPage - 1]) return; // Seite noch nicht geladen → später erneut
    lastJump.current = jumpNonce;
    scrollToPage(jumpPage - 1);
  }, [jumpNonce, jumpPage, pages.length]);

  const onScroll = () => {
    const cont = scrollRef.current;
    if (!cont) return;
    const contTop = cont.getBoundingClientRect().top;
    let cur = 1;
    for (let i = 0; i < pageRefs.current.length; i++) {
      const el = pageRefs.current[i];
      if (el && el.getBoundingClientRect().top - contTop <= 100) cur = i + 1;
    }
    setCurrent(cur);
  };

  const goto = (p) => {
    const idx = Math.max(1, Math.min(count, p));
    scrollToPage(idx - 1);
  };

  return (
    <div className="h-full flex flex-col min-h-0 bg-gray-200">
      <div className="shrink-0 flex items-center justify-center gap-3 px-3 py-1.5 border-b border-gray-300 bg-white">
        <button onClick={() => goto(current - 1)} disabled={current <= 1} data-testid="pdf-prev-page" title="Vorherige Seite"
          className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 hover:bg-gray-50">
          <ChevronUp className="h-4 w-4" />
        </button>
        <span className="text-xs font-semibold text-gray-700 tabular-nums" data-testid="pdf-page-indicator">
          Seite {current} / {count || "…"}
        </span>
        <button onClick={() => goto(current + 1)} disabled={!!count && current >= count} data-testid="pdf-next-page" title="Nächste Seite"
          className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 hover:bg-gray-50">
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
      <div ref={scrollRef} onScroll={onScroll} className="flex-1 min-h-0 overflow-y-auto p-3">
        <div className="max-w-3xl mx-auto space-y-3">
          {pages.map((src, i) => (
            <img key={i} ref={(el) => { pageRefs.current[i] = el; }} src={src} alt={`Seite ${i + 1}`}
              data-testid={`paged-pdf-page-${i}`} className="w-full shadow-sm border border-gray-300 bg-white" />
          ))}
          {pages.length === 0 && (
            <div className="py-24 flex flex-col items-center text-gray-400">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" />
                : <><FileText className="h-8 w-8" /><p className="mt-2 text-sm">{error ? "PDF nicht verfügbar." : "Keine Seiten."}</p></>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
